import React from "react";

/**
 * Diagram do równości odcinków stycznych – wersja „same styczne”.
 * - A: górny punkt styczności (tangenta pozioma).
 * - B: punkt styczności w DOLNO-PRAWEJ ćwiartce (domyślnie ~45°).
 * - P: przecięcie obu stycznych.
 * - PA i PB pogrubione; brak promieni/znaczników 90°.
 *
 * NOWE:
 * - showExpr: false → zamiast expr1/expr2 pokazujemy labelPA/labelPB (tryb teorii)
 * - SVG responsywne: viewBox + width:100% → brak scrolla poziomego
 */
export default function CircleEqualTangentsDiagram({
  width = 640,
  height = 360,
  padding = 28,
  circleR = 110,
  angleBdeg = 45, // 0..90 ⇒ B „niżej i z prawej”

  // zadania
  expr1 = "x+7",
  expr2 = "2x-1",

  // teoria
  showExpr = true,
  labelPA = "PA",
  labelPB = "PB",

  pbLabelFlip = true, // podpis przy PB po drugiej stronie linii + obrót 180°
}) {
  const W = width, H = height;
  const COLORS = {
    base: "currentColor",
    tangent: "#10b981",
    accent: "#f59e0b",
  };

  // położenie okręgu (Twoje)
  const cx = padding + circleR + 60;
  const cy = padding + circleR + 40;

  // punkty styczności
  const A = { x: cx, y: cy - circleR };
  const t = (angleBdeg * Math.PI) / 180;
  const B = { x: cx + circleR * Math.cos(t), y: cy + circleR * Math.sin(t) };

  // styczna w A: pozioma
  const horizY = A.y;

  // styczna w B: prosta prostopadła do OB
  const nx = B.x - cx, ny = B.y - cy;
  const m = -nx / ny; // nachylenie stycznej w B

  // przecięcie obu stycznych (P)
  const Px = B.x + (horizY - B.y) / m;
  const Py = horizY;
  const P = { x: Px, y: Py };

  // długie linie stycznych (kontekst)
  const longX1 = padding;
  const longX2 = W - padding;
  const yAt = (x) => B.y + m * (x - B.x);

  const longHoriz = { x1: longX1, y1: horizY, x2: longX2, y2: horizY };
  const longOblique = { x1: longX1, y1: yAt(longX1), x2: longX2, y2: yAt(longX2) };

  // środki odcinków
  const mid = (U, V) => ({ x: (U.x + V.x) / 2, y: (U.y + V.y) / 2 });
  const midPA = mid(P, A);
  const midPB = mid(P, B);

  // odsunięcie podpisu PB prostopadle do PB
  function perpOffset(U, V, len) {
    const vx = V.x - U.x, vy = V.y - U.y;
    let ox = -vy, oy = vx;
    const n = Math.hypot(ox, oy) || 1;
    ox /= n; oy /= n;
    if (oy < 0) { ox = -ox; oy = -oy; }
    return { dx: ox * len, dy: oy * len };
  }
  let offPB = perpOffset(P, B, 12);
  if (pbLabelFlip) offPB = { dx: -offPB.dx, dy: -offPB.dy };

  // obrót etykiety PB równolegle do PB
  const angPBdeg = (Math.atan2(B.y - P.y, B.x - P.x) * 180) / Math.PI;
  const angPBLabel = angPBdeg + (pbLabelFlip ? 180 : 0);

  const Label = ({ x, y, children, dx = 0, dy = 0, anchor = "middle" }) => (
    <text
      x={x + dx}
      y={y + dy}
      fill={COLORS.accent}
      fontSize="14"
      fontFamily="inherit"
      textAnchor={anchor}
      dominantBaseline="middle"
      pointerEvents="none"
    >
      {children}
    </text>
  );

  const textPA = showExpr ? expr1 : labelPA;
  const textPB = showExpr ? expr2 : labelPB;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      {/* okrąg + środek O */}
      <circle cx={cx} cy={cy} r={circleR} fill="none" stroke={COLORS.base} strokeWidth="2" />
      <circle cx={cx} cy={cy} r="3.2" fill={COLORS.base} />
      <Label x={cx} y={cy} dx={-10} anchor="end">O</Label>

      {/* długie linie stycznych */}
      <line {...longHoriz} stroke={COLORS.tangent} strokeWidth="3" />
      <line {...longOblique} stroke={COLORS.tangent} strokeWidth="3" />

      {/* odcinki PA i PB */}
      <line x1={P.x} y1={P.y} x2={A.x} y2={A.y} stroke={COLORS.tangent} strokeWidth="4" />
      <line x1={P.x} y1={P.y} x2={B.x} y2={B.y} stroke={COLORS.tangent} strokeWidth="4" />

      {/* punkty */}
      <circle cx={A.x} cy={A.y} r="4" fill={COLORS.base} />
      <circle cx={B.x} cy={B.y} r="4" fill={COLORS.base} />
      <circle cx={P.x} cy={P.y} r="4" fill={COLORS.base} />

      {/* etykiety punktów */}
      <Label x={A.x} y={A.y} dy={-12}>A</Label>
      <Label x={B.x} y={B.y} dx={12} anchor="start">B</Label>
      <Label x={P.x} y={P.y} dy={-12}>P</Label>

      {/* podpis PA: nad odcinkiem */}
      <Label x={midPA.x} y={midPA.y} dy={-12}>{textPA}</Label>

      {/* podpis PB: równolegle do PB */}
      <text
        x={midPB.x + offPB.dx}
        y={midPB.y + offPB.dy}
        transform={`rotate(${angPBLabel}, ${midPB.x + offPB.dx}, ${midPB.y + offPB.dy})`}
        fill={COLORS.accent}
        fontSize="14"
        fontFamily="inherit"
        textAnchor="middle"
        dominantBaseline="middle"
        pointerEvents="none"
      >
        {textPB}
      </text>
    </svg>
  );
}
